/**
 * The `plan_review` payload, normalized for rendering.
 *
 * Deliberately paranoid, and it has been right to be: the spec documented
 * `{target, locationRef, acceptance}`, a real captured frame carried
 * `{id, title, target_locationRef, description}`, and the harness now coerces the model's
 * arguments before the interrupt fires and sends `{work_item_id, description, target: {…}}`. Three
 * shapes for one thing, so the rule stays "render what is present": read every known spelling,
 * treat all of them as optional, and degrade to the raw JSON rather than to nothing.
 *
 * That last change moved every field this file keys on. The interrupt used to hand the maker the
 * model's raw arguments and validate them only on approval, so a plan could render perfectly and
 * fail the instant it was approved; the harness now runs the tool body's own coercion first.
 * Reading the old spellings alone left the spine with no location chip and no resolvable
 * dependencies — a plan that looked thinner than the one the agent proposed.
 */
import {
  HarnessApproval,
  HarnessPlanDisposition,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  HarnessPlanRecord,
  HarnessStoryPlanSnapshot,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import { planStateLabelOf } from '@flowx/designer/modules/ai-builder/types/plan-state.types'
import { toExecutionProgress } from '@flowx/designer/modules/ai-builder/utils/execution-progress.util'
import {
  HarnessOpenQuestion,
  toOpenQuestion,
} from '@flowx/designer/modules/ai-builder/utils/open-question.util'
import {
  asArray,
  asString,
  firstNonEmpty,
  isRecord,
  withRawFallback,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'
import {
  planUsesStories,
  toStoryPlanSnapshot,
} from '@flowx/designer/modules/ai-builder/utils/story-plan/story-plan-contract.util'
import { isUuid, unslug } from '@flowx/designer/modules/ai-builder/utils/text.util'

/** Where a work item lands, as the write plane addresses it. `resourceId` and `elementId` are
 * uuids and mean nothing to a maker, so they are kept apart from `label`: the label is what a step
 * wears, the ids are what an implementer needs. */
export type PlanTarget = {
  resourceType?: string
  resourceId?: string
  subResourcePath?: string
  elementType?: string
  elementId?: string
}

export type PlanWorkItem = {
  id?: string
  heading: string
  /** A short human phrase for the spine's `⌖` chip. */
  locationRef?: string
  target?: PlanTarget
  description?: string
  /** What is there today. Its absence is what makes an item ungrounded — see `ungrounded`. */
  currentBehaviour?: string
  /** How to tell the step landed — the harness's `acceptance_check`, the design's "How to test
   * this step" card. On the wire and dropped, because only the older `acceptance` spelling was
   * read and that one folded into the description. */
  acceptanceCheck?: string
  /** The item creates something rather than changing it, so having no "before" is correct. */
  creates: boolean
  dependsOn: readonly string[]
  /** The typed edit the harness can run for this step, when the model wrote one: `operationId` is
   * the registered operation (`process.node.rename`, …) and `fields` its arguments as the model
   * spelled them. Its presence is what makes a step runnable once the plan is READY. */
  execution?: PlanExecutionIntent
  /** The registered operation named by an untyped step, before its edit is filled at act time. */
  operationId?: string
}

export type PlanExecutionIntent = {
  operationId: string
  /** The intent's scalar arguments, in order, as `[name, value]` pairs a card can list. */
  fields: readonly (readonly [string, string])[]
}

/** A step that says what to change without saying what is there now. Reported by the harness,
 * never a refusal: approving anyway is the maker's call, and until now an uninformed one. */
export type PlanUngroundedItem = {
  workItemId: string
  description?: string
  missing?: string
}

export type PlanDisplay = {
  storyPlan?: HarnessStoryPlanSnapshot
  goal: string
  workItems: readonly PlanWorkItem[]
  openQuestions: readonly HarnessOpenQuestion[]
  constraints: readonly string[]
  ungrounded: readonly PlanUngroundedItem[]
  /** The harness's sentence about the ungrounded steps, in its own words. Shown rather than
   * re-derived: the count and the caveat belong together and it already wrote both. */
  ungroundedNote?: string
  /** Tier-1 check results the harness attached to this interrupt. Warnings only — anything it
   * refuses on never reaches a maker as a plan. */
  warnings: readonly string[]
  /** What the harness's own checks found wrong with the plan and could not fix. Rare on a plan a
   * maker sees — the turn ends as `planning_failed` when its repairs run out. */
  hardFailures: readonly string[]
  /** The harness's own view of whether the plan may be approved: false while an open question is
   * unresolved or unanswered. `undefined` means it did not say. */
  isApprovable?: boolean
  /** Set when the payload was not shaped like a plan at all. The card shows the raw JSON so a
   * maker sees something true rather than an empty screen. */
  raw?: string
}

export function toPlanDisplay(payload: unknown): PlanDisplay {
  if (!isRecord(payload)) {
    return withRawFallback(emptyPlan(), true, payload)
  }

  const storyPlan = planUsesStories(payload) ? toStoryPlanSnapshot(payload) : undefined
  const workItems = storyPlan ? [] : asArray(payload['work_items']).map(toWorkItem)

  const plan: PlanDisplay = {
    ...(storyPlan && { storyPlan }),
    goal: storyPlan ? (storyPlan.goal ?? '') : asString(payload['goal']),
    workItems,
    openQuestions: asArray(payload['open_questions']).map(toOpenQuestion),
    constraints: asArray(payload['constraints'])
      .map((value) => asString(value))
      .filter(Boolean),
    ungrounded: asArray(payload['ungrounded']).map(toUngrounded),
    ungroundedNote: asString(payload['ungrounded_note']) || undefined,
    warnings: asArray(payload['plan_warnings'])
      .map((value) => asString(value))
      .filter(Boolean),
    hardFailures: asArray(payload['hard_failures'])
      .map((value) => asString(value))
      .filter(Boolean),
    isApprovable:
      typeof payload['is_approvable'] === 'boolean' ? payload['is_approvable'] : undefined,
  }

  // Nothing recognisable came through, but the payload was an object — show it rather than an
  // empty plan, as though the agent proposed nothing.
  const isEmpty =
    !storyPlan &&
    !plan.goal &&
    !plan.workItems.length &&
    !plan.openQuestions.length &&
    !plan.constraints.length

  return withRawFallback(plan, isEmpty, payload)
}

/**
 * The plan the session shows. A chosen story snapshot is the whole plan: the latest payload only
 * supplies the review fields around it (open questions, warnings, hard failures). Without a
 * snapshot, a payload that uses stories shows nothing until its snapshot lands, rather than a plan
 * read field by field from the wrong source. Otherwise the payload's goal, questions and checks
 * sit over every step the plan holds (`planSteps`); before a run the payload is the whole plan.
 */
export function toSessionPlanDisplay(
  latestPlan: unknown,
  storyPlanSnapshot: HarnessStoryPlanSnapshot | null,
  plan: HarnessPlanRecord | null = null
): PlanDisplay | null {
  if (storyPlanSnapshot) {
    return {
      ...toPlanDisplay(latestPlan ?? { expansion: 'milestones' }),
      storyPlan: storyPlanSnapshot,
      goal: storyPlanSnapshot.goal ?? '',
      workItems: [],
      raw: undefined,
    }
  }
  const steps = plan ? planSteps(plan) : null
  const display = latestPlan
    ? toPlanDisplay(
        steps && isRecord(latestPlan) ? { ...latestPlan, work_items: steps } : latestPlan
      )
    : null

  return display?.storyPlan ? null : display
}

/**
 * Every step a plan holds once it has a run or a parked revision, else null. A proposal made then
 * only revises the steps not written yet and lists just those (the harness parks it as the plan's
 * amendment), so the steps come from the record: the written ones as they are, then the rest with
 * the parked revision folded in the way the harness will fold it.
 */
export function planSteps(plan: HarnessPlanRecord): readonly Record<string, unknown>[] | null {
  const steps = plan.work_item_payloads

  if (!(plan.execution || plan.amendment) || !steps?.length) {
    return null
  }

  const { completed } = toExecutionProgress(plan.execution)
  const written = steps.filter((step) => completed.has(stepId(step)))
  const unwritten = steps.filter((step) => !completed.has(stepId(step)))

  return [
    ...written,
    ...(plan.amendment ? withRevision(unwritten, plan.amendment.proposed_items) : unwritten),
  ]
}

/** The ids of every step the plan holds, for counting progress against. */
export function toPlanStepIds(plan: HarnessPlanRecord | null): readonly string[] {
  return (
    (plan && planSteps(plan)?.map(stepId)) ??
    (plan?.work_items ?? []).map((item) => item.work_item_id)
  )
}

/**
 * The unwritten steps with a revision applied, as the harness applies it (`with_omitted`): each
 * revised step in its place, and every step the revision does not restate kept where it was
 * planned. A revision that lists only its fix drops nothing.
 */
export function withRevision(
  remaining: readonly Record<string, unknown>[],
  revision: readonly Record<string, unknown>[]
): readonly Record<string, unknown>[] {
  const listed = new Set(revision.map(stepId))
  const kept = remaining.filter(
    (step) => !listed.has(stepId(step)) && !revision.some((revised) => isSameStep(revised, step))
  )
  const position = new Map(remaining.map((step, index) => [stepId(step), index]))
  type Fold = {
    placed: readonly Record<string, unknown>[]
    queue: readonly Record<string, unknown>[]
  }
  const { placed, queue } = revision.reduce<Fold>(
    (folded, revised) => {
      const at = position.get(stepId(revised))
      // The queue is in planned order, so the steps planned before this one are its head.
      const count =
        at === undefined
          ? 0
          : folded.queue.filter((step) => (position.get(stepId(step)) ?? at) < at).length

      return {
        placed: [...folded.placed, ...folded.queue.slice(0, count), revised],
        queue: folded.queue.slice(count),
      }
    },
    { placed: [], queue: kept }
  )

  return [...placed, ...queue]
}

export function stepId(step: Record<string, unknown>): string {
  return asString(step['work_item_id'])
}

/** The operation a step runs: its typed intent's, or an untyped step's own. */
export function stepOperation(step: Record<string, unknown>): string {
  return isRecord(step['execution'])
    ? asString(step['execution']['operation_id'])
    : asString(step['operation_id'])
}

/** Two steps that run the same operation on the same resource, the harness's `same_step`. */
function isSameStep(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const resourceOf = (step: Record<string, unknown>): string =>
    isRecord(step['target']) ? asString(step['target']['resource_id']) : ''

  return stepOperation(a) === stepOperation(b) && resourceOf(a) === resourceOf(b)
}

/** The ids of the steps the harness could not ground, for tagging them where they are read. The
 * note says "3 of 7 steps" without saying which three, and a maker approving knowingly needs the
 * second half more than the first. */
export function ungroundedIds(plan: PlanDisplay): ReadonlySet<string> {
  return new Set(plan.ungrounded.map((item) => item.workItemId))
}

/** One resource the plan lands on, and the step that lands there. */
export type PlanChange = {
  /** Zero-based position of the step in the plan, for "Step N". */
  index: number
  item: PlanWorkItem
  /** The resource type, when the address names one — `process`, `data-model`, … */
  resourceType?: string
  /** The best short name the payload has for the place: the chip's label, else the heading. */
  label: string
  /** What the step makes or changes, by name: the edit's own `name`, else the step's heading. */
  name: string
  /** What the step does, in the agent's words — the note after a created thing's name, and the
   * whole row for an edit, as the design sets them. */
  note: string
}

export type PlanChanges = {
  created: readonly PlanChange[]
  edited: readonly PlanChange[]
}

/**
 * What the plan changes, sorted into what it creates and what it edits. Read straight off the work
 * items: `creates` is the harness's own flag, and an item with an address that does not create is
 * an edit to what is there. An item with neither is left out rather than guessed at. No "deleted"
 * bucket — nothing on the wire says a step removes something, and inferring one from wording would
 * be a claim about the application rather than about the plan.
 */
export function planChanges(plan: PlanDisplay): PlanChanges {
  const changes = plan.workItems.flatMap((item, index): PlanChange[] => {
    const label = item.locationRef || item.heading
    const addressed = !!item.target?.resourceId || !!item.locationRef

    return item.creates || addressed
      ? [
          {
            index,
            item,
            resourceType: item.target?.resourceType || undefined,
            label,
            name: executionName(item) || item.heading,
            note: item.description ?? '',
          },
        ]
      : []
  })

  return {
    created: changes.filter((change) => change.item.creates),
    edited: changes.filter((change) => !change.item.creates),
  }
}

/** The name the step's typed edit gives what it makes — `name`, `display name` — when it has one. */
function executionName(item: PlanWorkItem): string {
  const field = item.execution?.fields.find(([key]) => /^(display )?name$/i.test(key))

  return field?.[1] ?? ''
}

/** Built once by the caller rather than per row: the spine asks for one step's dependencies at a
 * time, so rebuilding the index inside made it O(n²). `flatMap` rather than `filter` + `map`
 * because `filter` does not narrow, and `string | undefined` is not a key a lookup can produce. */
export function workItemHeadings(plan: PlanDisplay): ReadonlyMap<string, string> {
  return new Map(
    plan.workItems.flatMap((item) => (item.id ? [[item.id, item.heading] as const] : []))
  )
}

/** `depends_on` carries work-item ids, which are meaningless to a maker. Falls back to the raw id
 * when nothing matches — a dangling reference is worth showing, not hiding. */
export function resolveDependencyLabels(
  headings: ReadonlyMap<string, string>,
  item: PlanWorkItem
): readonly string[] {
  return item.dependsOn.map((id) => headings.get(id) ?? id)
}

/** The plan's lifecycle as one short label: `DRAFT · description rev 2`, `READY · v1`,
 * `ABANDONED`, `implemented`. The harness's own words, lower-cased for the rail. Two counters mean
 * different things — a description edit increments `description_revision`, and only a validated set
 * of work items makes a plan version — so a DRAFT names its description revision and a READY plan
 * its version, never both. Empty on a session the harness never indexed. */
export function planStateLabel(
  plan: HarnessPlanRecord | null,
  disposition: HarnessPlanDisposition | null = null
): string {
  const state = planStateName(plan, disposition)

  if (!plan || plan.execution) {
    return state
  }

  switch (plan.state) {
    case 'READY':
    case 'EXECUTING':
    case 'PAUSED':
    case 'FAILED':
    case null:
      return `${state} · v${plan.version}`
    case 'ABANDONED':
    case 'COMPLETED':
      return state
    default:
      return plan.description_revision > 1
        ? `${state} · description rev ${plan.description_revision}`
        : state
  }
}

/** The plan's state alone, without the counter `planStateLabel` adds to it: `ready`, `run failed`,
 * `implemented`. Empty on a session the harness never indexed. */
export function planStateName(
  plan: HarnessPlanRecord | null,
  disposition: HarnessPlanDisposition | null = null
): string {
  return plan
    ? planStateLabelOf(
        {
          ...plan,
          run_status: plan.execution?.status,
          stop_code: plan.execution?.stop?.code || plan.execution?.error_code,
        },
        disposition
      )
    : ''
}

function toWorkItem(value: unknown, index: number): PlanWorkItem {
  if (!isRecord(value)) {
    return { heading: asString(value) || `Work item ${index + 1}`, creates: false, dependsOn: [] }
  }

  const target = toTarget(value)

  // Every spelling seen so far, most specific first. `acceptance` folds into the description
  // because both answer "what does this item amount to" and only one ever appears. The id is a slug
  // the harness minted from the description, so it is read back as words when it is all there is to
  // head a step with — but a uuid is not that, and 36 characters of hex falls through to the
  // position instead.
  const rawId = firstNonEmpty(asString(value['work_item_id']), asString(value['id']))
  const heading = firstNonEmpty(
    asString(value['title']),
    asString(value['target']),
    isUuid(rawId) ? '' : unslug(rawId),
    `Work item ${index + 1}`
  )

  return {
    // `work_item_id` first: the harness mints it now, and it is what `depends_on` points at.
    // Reading only `id` left every dependency dangling and every "after N" unresolvable.
    id: rawId || undefined,

    heading,
    locationRef:
      firstNonEmpty(
        asString(value['target_locationRef']),
        asString(value['locationRef']),
        asString(value['target']),
        locationLabel(target) ?? ''
      ) || undefined,
    target,
    description:
      firstNonEmpty(
        asString(value['description']),
        asString(value['proposed_change']),
        asString(value['acceptance'])
      ) || undefined,
    currentBehaviour: asString(value['current_behaviour']) || undefined,
    acceptanceCheck: asString(value['acceptance_check']) || undefined,
    creates: value['creates'] === true,
    dependsOn: asArray(value['depends_on'])
      .map((dependency) => asString(dependency))
      .filter(Boolean),
    execution: toExecutionIntent(value['execution']),
    operationId: asString(value['operation_id']) || undefined,
  }
}

/** Only scalars are listed: a nested list (an enumeration's values, a data type's attributes) is
 * summarised by its count, because the card reading this is a caption for the step and not a form
 * — the run's own preview is where the full values are shown. */
function toExecutionIntent(value: unknown): PlanExecutionIntent | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const operationId = asString(value['operation_id'])

  if (!operationId) {
    return undefined
  }

  const fields = Object.entries(value)
    .filter(([key]) => key !== 'operation_id')
    .flatMap(([key, entry]): (readonly [string, string])[] => {
      if (Array.isArray(entry)) {
        return [[unslug(key), `${entry.length} ${entry.length === 1 ? 'entry' : 'entries'}`]]
      }

      const text = asString(entry)

      return text ? [[unslug(key), text]] : []
    })

  return { operationId, fields }
}

/**
 * The nested address, from the shape the harness now sends and the flat one models still write —
 * both, because coercion happens harness-side and a payload that skipped it (a raw frame, an older
 * service) still carries `resource_id`/`element_type` at the top level.
 *
 * **The asymmetry below is the wire's, not a copy/paste slip.** `resource_*` is read nested-first
 * and `element_*` top-level-first because that is where the harness puts them
 * (`docs/ai-builder/m1-plan-review-contract.md:35-40`). Said here because it reads as a bug on
 * every first look.
 */

function toTarget(value: Record<string, unknown>): PlanTarget | undefined {
  const nested = isRecord(value['target']) ? value['target'] : {}
  const pick = (key: string, preference: 'nested' | 'flat'): string | undefined => {
    const preferred = preference === 'nested' ? nested : value
    const fallback = preference === 'nested' ? value : nested

    return firstNonEmpty(asString(preferred[key]), asString(fallback[key])) || undefined
  }

  const target: PlanTarget = {
    resourceType: pick('resource_type', 'nested'),
    resourceId: pick('resource_id', 'nested'),
    subResourcePath: pick('sub_resource_path', 'nested'),
    elementType: pick('element_type', 'flat'),
    elementId: pick('element_id', 'flat'),
  }

  return Object.values(target).some(Boolean) ? target : undefined
}

/** The `⌖` chip's text: resource types and paths, never uuids. A resource id is the only *name* this
 * payload carries — the grounding that resolves ids to names rides on the brief interrupt — so a
 * chip built from it would trade a readable label for 36 characters of hex. The ids are still
 * shown in the step's detail, where they are the write address rather than a caption. */
function locationLabel(target: PlanTarget | undefined): string | undefined {
  if (!target) {
    return undefined
  }

  const parts = [target.resourceType, target.subResourcePath || target.elementType].filter(Boolean)

  return parts.length ? parts.join(' › ') : undefined
}

function toUngrounded(value: unknown, index: number): PlanUngroundedItem {
  if (!isRecord(value)) {
    return { workItemId: asString(value) || `item ${index + 1}` }
  }

  return {
    workItemId: asString(value['work_item_id']) || `item ${index + 1}`,
    description: asString(value['description']) || undefined,
    missing: asString(value['missing']) || undefined,
  }
}

function emptyPlan(): PlanDisplay {
  return {
    goal: '',
    workItems: [],
    openQuestions: [],
    constraints: [],
    ungrounded: [],
    warnings: [],
    hardFailures: [],
  }
}

/** Who approved a plan version, and how — the four ways the harness records one (D6). The two
 * human ones are kept apart on purpose: approving a plan is not the same as a change having been
 * confirmed by the request that asked for it. */
export function approvalLabel(approval: HarnessApproval | null | undefined): string | null {
  if (!approval) {
    return null
  }

  const who = approval.display_name || approval.username || 'you'

  switch (approval.type) {
    case 'explicit':
      return `Approved by ${who}`
    case 'request':
      return `Requested by ${who}`
    case 'envelope':
      return 'Adjusted by the agent within what was approved'
    default:
      return 'Approved before approvers were recorded'
  }
}

export const BRANCH_OUTCOME_LABEL: Readonly<Record<HarnessPlanDisposition, string>> = {
  awaiting_merge: 'awaiting merge',
  merged: 'merged',
  discarded: 'discarded',
  abandoned: 'abandoned',
  unknown: 'branch status unknown',
}
